import {Component, Inject, Injectable, OnDestroy, OnInit} from '@angular/core';
import {
    MAT_DIALOG_DATA,
    MatDialogRef,
    MatDialog,
} from '@angular/material/dialog';
import {SessionManagerService} from '@services/session/session-manager.service';

@Component({
    selector: 'app-logout',
    templateUrl: './logout.component.html',
    styleUrls: ['./logout.component.scss'],
    standalone: false
})
@Injectable(
    {
        providedIn: 'root',
    }
)
export class LogoutComponent implements OnInit, OnDestroy {
    message = '';
    countDown = Math.ceil(SessionManagerService.SESSION_GRACE_THRESHOLD / 1000);
    countdownInterval: any;
    previousTitle = document.title;

    private readonly LAST_COUNTDOWN_THRESHOLD = 5000; // 5 seconds
    private readonly ACTION_CONTINUE = 'continue';
    private readonly ACTION_DONE = 'done';
    public title = 'Session Expiring';
    private lastCountDownToggle: number = Date.now();

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: any,
        private dialogRef: MatDialogRef<LogoutComponent>,
        public dialog: MatDialog,
        private sessionManagerService: SessionManagerService,
    ) {
        if (data) {
            this.message = data.message || this.message;
            this.countDown = data.countdown || this.countDown;
        }
    }

    private checkCountDown(): void {
        this.countDown = this.getCountdown();

        const timeRemaining = this.sessionManagerService.getTimeRemaining();
        const titleThreshold = 10;
        const expiringMessage = '!! Session Expiring Soon !!';

        if (this.countDown <= 0 || timeRemaining <= 0) {
            this.setModalTitle('Session Expired');
            document.title = 'Session Expired';
            this.setModalContent('Your session has expired. Please log in again.');

            clearInterval(this.countdownInterval);

            // don't wait for a click, or the dialog stays up and blocks the login screen
            this.ok();
        } else if (!this.sessionManagerService.withinGracePeriod() && timeRemaining >= SessionManagerService.SESSION_GRACE_THRESHOLD) {
            // no logout, close countdown, session likely active
            this.handleAction({action: this.ACTION_CONTINUE});
        } else if (this.countDown <= titleThreshold) {
            // show countdown in title
            document.title = `${this.countDown} seconds til session expires`;
        } else if (document.title !== expiringMessage && (this.lastCountDownToggle + this.LAST_COUNTDOWN_THRESHOLD) < Date.now()) {
            document.title = expiringMessage;
            this.lastCountDownToggle = Date.now();
        } else if ((this.lastCountDownToggle + this.LAST_COUNTDOWN_THRESHOLD * 2) < Date.now()) {
            // Revert to the previous title after another full cycle
            document.title = this.previousTitle;
            this.lastCountDownToggle = Date.now();
        }
    }

    private getCountdown(): number {
        return Math.min(
            Math.ceil(SessionManagerService.SESSION_GRACE_THRESHOLD / 1000),
            Math.ceil(this.sessionManagerService.getTimeRemaining() / 1000)
        );
    }

    logout(): void {
        this.handleAction({action: this.ACTION_DONE});
    }

    handleAction(e: any): void {
        const {action} = e;

        if (action === this.ACTION_DONE) {
            this.dialogRef.close(true);
        } else if (action === this.ACTION_CONTINUE) {
            this.dialogRef.close(false);
        }
    }

    keepWorking(): void {
        try {
            this.sessionManagerService.refresh();

            this.handleAction({action: this.ACTION_CONTINUE});
        } catch (error: any) {
            this.handleAction({action: this.ACTION_DONE});
        }
    }

    setModalContent(textContent: string): void
    {
        const logoutModalElement = document.getElementById('logout-modal');

        if (!logoutModalElement) {
            return;
        }

        logoutModalElement.textContent = textContent || logoutModalElement.textContent;
    }

    setModalTitle(title: string): void
    {
        this.title = title || this.title;
    }

    ok(): void {
        const timeRemaining = this.sessionManagerService.getTimeRemaining();

        if (this.countDown <= 0 || timeRemaining <= 0) {
            this.handleAction({action: this.ACTION_DONE});
            return;
        }

        this.setModalContent('Session has since been renewed. You can continue working after this modal closes automatically.');

        setTimeout(() => {
            this.handleAction({action: this.ACTION_CONTINUE});
        }, 2000);
    }

    ngOnInit(): void {
        this.countDown = this.getCountdown();

        clearInterval(this.countdownInterval);

        this.checkCountDown();
        this.countdownInterval = setInterval( () => this.checkCountDown(), 100);
    }

    ngOnDestroy(): void {
        clearInterval(this.countdownInterval);
    }
}
